import type { Ref } from "vue";
import type { ResourceKeyFactory } from "../Query/resourceKeys";
import { iriToId } from "@verbleif/lib/src/composables/Utils/iri.helpers";
import { computed, ref, watch } from "vue";
import { useAuthStore } from "../AuthStore/useAuthStore";
import { bindQueryToRef } from "../Query/bindQueryToRef";
import { defineHydraSource } from "../Query/defineHydraSource";
import { invalidateResource } from "../Query/invalidateResource";
import { useResourceScope } from "../Query/resourceKeys";
import { notifyEffectiveViewsError } from "../TaskViews/notifyEffectiveViewsError";
import { sortViewRows } from "./viewsSettings.helpers";

/** The fields every effective view of a user at a location carries, for tasks and reports alike. */
export interface EffectiveViewStateItem {
  key: string
  enabled: boolean
  position: number
  fixed: boolean
  /** Whether the list switcher shows for the user at this location; the same on every item. */
  switcherEnabled?: boolean
  /** Whether view management (model A) applies for the user at this location; the same on every item. */
  viewManagement?: boolean
}

export interface EffectiveViewsStateOptions {
  keys: ResourceKeyFactory
  /** The collection under `/users/{userId}/locations/{locationId}/`, e.g. `effective_task_views`. */
  collection: string
  errorTitleKey: string
  errorFallbackTitle: string
}

function waitUntil(predicate: () => boolean): Promise<void> {
  if (predicate()) {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    const stop = watch(predicate, (ready) => {
      if (!ready) {
        return;
      }
      stop();
      resolve();
    }, { immediate: true });
  });
}

/**
 * The effective views of the signed-in user at the selected location, with the list settings the API resolved on
 * them: view management and the list switcher. Tasks and reports each keep one global instance of this.
 */
export function createEffectiveViewsState<T extends EffectiveViewStateItem>(options: EffectiveViewsStateOptions) {
  const scope = useResourceScope();
  const { user } = useAuthStore();
  const loading = ref(false);
  const views = ref([]) as Ref<T[]>;

  const queryEnabled = computed(() => {
    const userId = user.value?.id ?? null;
    const locationId = scope.value.location ? iriToId(scope.value.location) : null;
    return userId != null && locationId != null;
  });

  const { whenSettled } = bindQueryToRef(views, () => {
    const userId = user.value?.id ?? null;
    const locationId = scope.value.location ? iriToId(scope.value.location) : null;
    const queryKey = [...options.keys.all(scope.value), userId];
    const source = defineHydraSource<T>(
      queryKey,
      `/users/${userId}/locations/${locationId}/${options.collection}`,
    );
    return {
      queryKey,
      enabled: queryEnabled.value,
      retry: false,
      queryFn: async ({ signal }) => {
        const data = await source.load(new URLSearchParams(), signal);
        return data.member ?? [];
      },
      onError: error => notifyEffectiveViewsError(error, options.errorTitleKey, options.errorFallbackTitle),
    };
  }, [], loading);

  const enabledViews = computed(() => sortViewRows(views.value.filter(view => view.enabled)));

  /**
   * Whether view management (model A) applies to the user at this location: the same on every
   * effective view. Off while the views are not loaded, so the list then works as without it.
   */
  const viewManagement = computed(() => views.value.some(view => view.viewManagement === true));

  /** Whether the list switcher shows; only read with view management on. */
  const switcherEnabled = computed(() => !views.value.some(view => view.switcherEnabled === false));

  const byKey = computed(() => {
    const map = new Map<string, T>();
    for (const view of views.value) {
      map.set(view.key, view);
    }
    return map;
  });

  function refresh() {
    return invalidateResource(options.keys);
  }

  async function whenReady(): Promise<void> {
    await waitUntil(() => queryEnabled.value);
    await whenSettled();
  }

  return {
    loading,
    views,
    enabledViews,
    viewManagement,
    switcherEnabled,
    byKey,
    refresh,
    whenReady,
  };
}
